import { MIN_YEAR } from '@geosport/shared';
import { useId } from 'react';

interface Props {
  value: number | null;
  max: number;
  onChange: (year: number) => void;
}

/**
 * Accessible year slider (PRD §4.2, §10): starts neutral, arrow keys and ±1 buttons adjust,
 * aria-valuetext announces the year.
 */
export function YearSlider({ value, max, onChange }: Props) {
  const id = useId();
  const mid = Math.round((MIN_YEAR + max) / 2);
  const shown = value ?? mid;
  const nudge = (d: number): void => onChange(Math.min(max, Math.max(MIN_YEAR, shown + d)));

  return (
    <div className="flex flex-col gap-3">
      <div className="text-center" aria-live="polite">
        {value === null ? (
          <span className="text-lg text-white/60">Drag to pick a year</span>
        ) : (
          <span className="text-5xl font-bold tabular-nums">{value}</span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => nudge(-1)}
          aria-label="−1 year"
          className="min-h-11 min-w-11 rounded-lg bg-white/10 text-lg font-bold hover:bg-white/15"
        >
          −1
        </button>
        <input
          id={id}
          type="range"
          min={MIN_YEAR}
          max={max}
          step={1}
          value={shown}
          onChange={(e) => onChange(Number(e.target.value))}
          aria-label="Year"
          aria-valuemin={MIN_YEAR}
          aria-valuemax={max}
          aria-valuenow={shown}
          aria-valuetext={value === null ? 'No year selected' : String(value)}
          className={`h-11 w-full accent-accent ${value === null ? 'opacity-60' : ''}`}
        />
        <button
          type="button"
          onClick={() => nudge(1)}
          aria-label="+1 year"
          className="min-h-11 min-w-11 rounded-lg bg-white/10 text-lg font-bold hover:bg-white/15"
        >
          +1
        </button>
      </div>
      <div className="flex justify-between text-xs text-white/40 tabular-nums" aria-hidden="true">
        <span>{MIN_YEAR}</span>
        <span>{max}</span>
      </div>
    </div>
  );
}
